import { inject, Injectable } from '@angular/core';

import { createAppKit, type CreateAppKit } from '@reown/appkit';
import { type AppKitNetwork, base, baseSepolia, mainnet } from '@reown/appkit/networks';
import { EthersAdapter } from '@reown/appkit-adapter-ethers';

import { BROWSER_WALLET_GLOBAL } from '@drf/core';
import { ReownAppKitNetworkConfig } from '@drf/environments';

import { WalletConnectionError, WalletConnectionErrorCode } from '../models';
import { REOWN_APPKIT_CONFIG, REOWN_APPKIT_MODAL, REOWN_APPKIT_NETWORKS, ReownAppKitAccess, ReownAppKitModal } from '../tokens';

@Injectable({ providedIn: 'root' })
export class ReownAppKitService implements ReownAppKitAccess {
  readonly #browserGlobal = inject(BROWSER_WALLET_GLOBAL);
  readonly #config = inject(REOWN_APPKIT_CONFIG);
  readonly #modalOverride = inject(REOWN_APPKIT_MODAL);
  readonly #networks = inject(REOWN_APPKIT_NETWORKS);

  #modal: ReownAppKitModal | null = null;

  getCachedModal(): ReownAppKitModal | null {
    return this.#modalOverride ?? this.#modal;
  }

  async getModal(): Promise<ReownAppKitModal | null> {
    if (this.#modalOverride) {
      return this.#modalOverride;
    }

    if (!this.#config.projectId.trim()) {
      return null;
    }

    if (!this.#modal) {
      this.#modal = this.#createModal();
    }

    return this.#modal;
  }

  isConfigured(): boolean {
    return this.#modalOverride !== null || this.#config.projectId.trim().length > 0;
  }

  async requireModal(): Promise<ReownAppKitModal> {
    const modal = await this.getModal();

    if (!modal) {
      throw new WalletConnectionError(WalletConnectionErrorCode.ProviderUnavailable, 'Wallet connection is unavailable.');
    }

    return modal;
  }

  #createModal(): ReownAppKitModal {
    const networks = this.#resolveAppKitNetworks();
    const metadataUrl = this.#resolveMetadataUrl();
    const options: CreateAppKit = {
      adapters: [new EthersAdapter()],
      allowUnsupportedChain: false,
      defaultNetwork: networks[0],
      enableReconnect: this.#config.enableReconnect,
      features: {
        analytics: this.#config.analytics,
        email: false,
        history: false,
        onramp: false,
        send: false,
        socials: false,
        swaps: false,
      },
      metadata: {
        ...this.#config.metadata,
        icons: this.#config.metadata.icons,
        url: metadataUrl,
      },
      networks,
      projectId: this.#config.projectId.trim(),
      themeMode: 'light',
    };

    return createAppKit(options);
  }

  #resolveAppKitNetworks(): [AppKitNetwork, ...AppKitNetwork[]] {
    const networks: AppKitNetwork[] = this.#networks.map(resolveReownAppKitNetwork);

    assertAppKitNetworksConfigured(networks);

    return networks;
  }

  #resolveMetadataUrl(): string {
    return this.#config.metadata.url.trim() || this.#browserGlobal.location?.origin || '';
  }
}

export function resolveReownAppKitNetwork(network: ReownAppKitNetworkConfig): AppKitNetwork {
  if (network.chainId === mainnet.id) {
    return mainnet;
  }

  if (network.chainId === baseSepolia.id) {
    return baseSepolia;
  }

  if (network.chainId === base.id) {
    return base;
  }

  throw new WalletConnectionError(WalletConnectionErrorCode.UnsupportedChain, `Unsupported Reown AppKit network: ${network.chainId}`);
}

function assertAppKitNetworksConfigured(networks: AppKitNetwork[]): asserts networks is [AppKitNetwork, ...AppKitNetwork[]] {
  if (networks.length === 0) {
    throw new WalletConnectionError(WalletConnectionErrorCode.UnsupportedChain, 'At least one Reown AppKit network must be configured.');
  }
}
